// Contrato dos blocos da vitrine modular — espelho, no front, do TypeBox
// definido na back#99.
//
// Onde mora: a issue #105 fala em "contracts.ts ganha BlockSchema". Ficou aqui
// porque este módulo importa `ShowcaseCardSchema`/`HeroSlideSchema` de
// `@/contracts` — colocá-lo lá dentro criaria um ciclo, e `contracts.ts` já tem
// 1156 linhas. A direção de importação é só uma: showcases → contracts.
//
// REGRA DA CASA aplicada aqui: **default em todo campo opcional**. Uma vitrine
// gravada antes deste formato existir precisa continuar abrindo a tela; campo
// ausente vira valor neutro, não `undefined` espalhado pelo renderizador.
import { z } from "zod";
import { HeroSlideSchema, ShowcaseAppearanceSchema, ShowcaseCardSchema } from "@/contracts";

// Tipos de bloco, espelhando a back#99.
//
// O bloco de conteúdo livre é `markdown`, não `html` — decisão 1 registrada na
// #99. O NOME importa: um tipo chamado `html` que não aceita HTML é um contrato
// que mente, e é o que leva alguém, meses depois, a "consertar" o renderizador.
//
// A segurança depende dos DOIS lados. Aqui o conteúdo é texto; quem desenha é o
// `react-markdown` de `lib/markdown.tsx`, que roda SEM `rehype-raw` — então HTML
// cru no corpo é inerte. Ligar `rehype-raw` lá reabre exatamente o XSS que esta
// decisão fecha.
export const BLOCK_TYPES = ["featured", "banner", "cards", "dynamic", "pills", "markdown"] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

const HEX = /^#[0-9a-fA-F]{6}$/;

// As quatro abas do drawer do editor mapeiam 1:1 nestes ramos — é o que mantém
// editor e renderizador em sincronia.
export const BlockTitleSchema = z.object({
  text: z.string().default(""),
  align: z.enum(["left", "center", "right"]).default("left"),
  visible: z.boolean().default(true),
});
export type BlockTitle = z.infer<typeof BlockTitleSchema>;

export const BlockStyleSchema = z.object({
  background: z.string().regex(HEX).nullable().default(null),
  padding: z.enum(["sm", "md", "lg"]).default("md"),
  radius: z.enum(["none", "sm", "lg"]).default("sm"),
  overlayOpacity: z.number().min(0).max(1).nullable().default(null),
});
export type BlockStyle = z.infer<typeof BlockStyleSchema>;

export const BlockLayoutSchema = z.object({
  mode: z.enum(["grid", "carousel"]).default("carousel"),
  columns: z.number().int().min(2).max(6).default(4),
  align: z.enum(["left", "center"]).default("left"),
  // Proporção do card: `portrait` serve às artes verticais (capas de pôster).
  cardFormat: z.enum(["landscape", "portrait"]).default("landscape"),
});
export type BlockLayout = z.infer<typeof BlockLayoutSchema>;

// ATENÇÃO ao consumir: isto CONFIGURA a regra, não a aplica. Quem filtra é o
// servidor (back#99, decisão 2) — o read-model não pode mandar para o cliente
// aquilo que ele deveria esconder. Esconder no cliente não protege nada: basta
// abrir o DevTools. A UI do editor não pode sugerir o contrário.
export const BlockVisibilitySchema = z.object({
  roles: z.enum(["mentorado", "mentor", "admin"]).array().default([]),
  authState: z.enum(["any", "authenticated", "anonymous"]).default("any"),
});
export type BlockVisibility = z.infer<typeof BlockVisibilitySchema>;

// --- `data` por tipo ---

// `featured` e `cards` são card-like: reusam o card que a vitrine já entende.
export const FeaturedDataSchema = z.object({
  items: ShowcaseCardSchema.array().default([]),
});
export const CardsDataSchema = FeaturedDataSchema;

// `banner` estende o slide atual com vídeo (a proporção já existia).
export const BannerDataSchema = z.object({
  slides: HeroSlideSchema.extend({
    videoUrl: z.string().nullable().default(null),
  })
    .array()
    .default([]),
});

// `dynamic` substitui os antigos courses/programs/posts/spaces/continue.
export const DYNAMIC_SOURCES = ["courses", "programs", "posts", "continue", "spaces"] as const;
export type DynamicSource = (typeof DYNAMIC_SOURCES)[number];
export const DynamicDataSchema = z.object({
  source: z.enum(DYNAMIC_SOURCES),
  category: z.string().nullable().default(null),
  programId: z.string().nullable().default(null),
  limit: z.number().int().min(1).max(50).nullable().default(null),
});

export const PillsDataSchema = z.object({
  items: z
    .object({
      id: z.string(),
      label: z.string(),
      icon: z.string().nullable().default(null),
      color: z.string().regex(HEX).nullable().default(null),
      href: z.string().nullable().default(null),
    })
    .array()
    .default([]),
});

// Texto, não markup. O teto espelha o do back (20 000): o corpo vive num jsonb
// lido a cada abertura da vitrine.
export const MarkdownDataSchema = z.object({
  content: z.string().max(20000).default(""),
});

// --- Envelope ---

// `type` é string livre, não enum — mesma razão documentada em
// `blocks/section-view.tsx`: um enum no parse derruba a VITRINE INTEIRA no
// primeiro tipo desconhecido, e é justamente a migração do back que introduz
// tipos novos. Tolerar aqui e despachar explicitamente lá é o que degrada bem.
// `data` fica `unknown` no envelope e é estreitado por `parseBlockData`.
// `.default()` do Zod devolve o valor LITERAL sem passar pelo schema — um
// `.default({})` aqui entregaria `{}` cru ao renderizador, sem os defaults dos
// campos de dentro, que é justamente o que este contrato promete evitar. Por
// isso o default é uma função que parseia `{}` e devolve o ramo já preenchido.
const ramoPadrao = <T extends z.ZodTypeAny>(schema: T) => () => schema.parse({}) as z.infer<T>;

export const BlockSchema = z.object({
  id: z.string(),
  type: z.string(),
  order: z.number().int().default(0),
  title: BlockTitleSchema.default(ramoPadrao(BlockTitleSchema)),
  style: BlockStyleSchema.default(ramoPadrao(BlockStyleSchema)),
  layout: BlockLayoutSchema.default(ramoPadrao(BlockLayoutSchema)),
  visibility: BlockVisibilitySchema.default(ramoPadrao(BlockVisibilitySchema)),
  data: z.unknown().default(null),
});
export type Block = z.infer<typeof BlockSchema>;

const DATA_BY_TYPE = {
  featured: FeaturedDataSchema,
  cards: CardsDataSchema,
  banner: BannerDataSchema,
  dynamic: DynamicDataSchema,
  pills: PillsDataSchema,
  markdown: MarkdownDataSchema,
} as const;

export type BlockData =
  | { type: "featured"; data: z.infer<typeof FeaturedDataSchema> }
  | { type: "cards"; data: z.infer<typeof CardsDataSchema> }
  | { type: "banner"; data: z.infer<typeof BannerDataSchema> }
  | { type: "dynamic"; data: z.infer<typeof DynamicDataSchema> }
  | { type: "pills"; data: z.infer<typeof PillsDataSchema> }
  | { type: "markdown"; data: z.infer<typeof MarkdownDataSchema> };

// Estreita o `data` de um bloco pelo seu tipo. Devolve `null` quando o tipo é
// desconhecido OU quando o `data` não bate com o formato — nos dois casos o
// renderizador pula o bloco, em vez de quebrar a vitrine inteira.
export function parseBlockData(block: Block): BlockData | null {
  const schema = DATA_BY_TYPE[block.type as BlockType];
  if (!schema) return null;
  const parsed = schema.safeParse(block.data ?? {});
  if (!parsed.success) return null;
  return { type: block.type as BlockType, data: parsed.data } as BlockData;
}

// --- A vitrine como registro (listagem e editor do admin) ---

export const SHOWCASE_STATUS = ["draft", "private", "public"] as const;
export type ShowcaseStatus = (typeof SHOWCASE_STATUS)[number];
export const ShowcaseStatusSchema = z.enum(SHOWCASE_STATUS);

// Nome diferente de `ShowcaseSchema` (contracts.ts) de propósito: aquele é o
// READ-MODEL que a vitrine pública consome, este é o REGISTRO que o admin edita.
// Confundir os dois é o caminho para a listagem tentar desenhar cards.
export const ShowcaseRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: ShowcaseStatusSchema.catch("draft"),
  isDefault: z.boolean().default(false),
  sections: BlockSchema.array().default([]),
  appearance: ShowcaseAppearanceSchema.default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ShowcaseRecord = z.infer<typeof ShowcaseRecordSchema>;
